/**
 * Compliance Center (Phase 9E Final)
 * Complete compliance management UI for suppression, consent, and audit logs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, ShieldAlert, ShieldCheck, Mail, Phone, Search, Trash2, Plus, FileText, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComplianceCenter = ({ user }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [suppressionList, setSuppressionList] = useState([]);
  const [consentRecords, setConsentRecords] = useState([]);
  const [complianceLogs, setComplianceLogs] = useState([]);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [outreachCounts, setOutreachCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newSuppression, setNewSuppression] = useState({ email: '', phone: '', reason: '' });
  const [isAddConsentDialogOpen, setIsAddConsentDialogOpen] = useState(false);
  const [newConsent, setNewConsent] = useState({ email: '', phone: '', consent_type: 'opt-in', consent_method: 'manual', consent_notes: '' });

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchDashboardMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${API}/compliance/dashboard`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setDashboardMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching compliance dashboard:', error);
    }
  }, [authHeaders]);

  const fetchSuppressionList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/compliance/suppression?search=${encodeURIComponent(searchQuery)}&limit=100`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSuppressionList(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching suppression list:', error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, searchQuery]);

  const fetchConsentRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/compliance/consent?email=${encodeURIComponent(searchQuery)}&limit=100`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConsentRecords(data.records || []);
      }
    } catch (error) {
      console.error('Error fetching consent records:', error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, searchQuery]);

  const fetchComplianceLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/compliance/logs?email=${encodeURIComponent(searchQuery)}&limit=100`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setComplianceLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching compliance logs:', error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, searchQuery]);

  // Outreach approval queue counts (backend/outreach_routes.py). A role
  // without the revenue_engine module gets a 403 here and the banner just
  // omits the counts.
  const fetchOutreachCounts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/outreach/drafts?status=pending_approval&limit=1`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setOutreachCounts(data.counts || null);
      }
    } catch (error) {
      console.error('Error fetching outreach approval counts:', error);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchDashboardMetrics();
    fetchOutreachCounts();
  }, [fetchDashboardMetrics, fetchOutreachCounts]);

  useEffect(() => {
    if (activeTab === 'suppression') fetchSuppressionList();
    if (activeTab === 'consent') fetchConsentRecords();
    if (activeTab === 'logs') fetchComplianceLogs();
  }, [activeTab, searchQuery, fetchSuppressionList, fetchConsentRecords, fetchComplianceLogs]);

  const handleAddSuppression = async () => {
    if (!newSuppression.email && !newSuppression.phone) {
      toast.error('Email or phone required');
      return;
    }

    try {
      const response = await fetch(`${API}/compliance/suppression`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ data: newSuppression })
      });

      if (response.ok) {
        toast.success('Added to suppression list');
        setIsAddDialogOpen(false);
        setNewSuppression({ email: '', phone: '', reason: '' });
        fetchSuppressionList();
        fetchDashboardMetrics();
      } else {
        toast.error('Failed to add suppression');
      }
    } catch (error) {
      toast.error('Error adding suppression');
    }
  };

  const handleRemoveSuppression = async (id) => {
    if (!confirm('Remove this entry from suppression list?')) return;

    try {
      const response = await fetch(`${API}/compliance/suppression/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        toast.success('Removed from suppression list');
        fetchSuppressionList();
        fetchDashboardMetrics();
      } else {
        toast.error('Failed to remove suppression');
      }
    } catch (error) {
      toast.error('Error removing suppression');
    }
  };

  const handleAddConsentRecord = async () => {
    if (!newConsent.email && !newConsent.phone) {
      toast.error('Email or phone required');
      return;
    }

    try {
      const response = await fetch(`${API}/compliance/consent`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ data: { ...newConsent, source: 'admin' } })
      });

      if (response.ok) {
        toast.success('Consent record logged');
        setIsAddConsentDialogOpen(false);
        setNewConsent({ email: '', phone: '', consent_type: 'opt-in', consent_method: 'manual', consent_notes: '' });
        fetchConsentRecords();
        fetchDashboardMetrics();
      } else {
        toast.error('Failed to log consent record');
      }
    } catch (error) {
      toast.error('Error logging consent record');
    }
  };

  const MetricCard = ({ icon: Icon, label, value, color = 'text-white' }) => (
    <Card className="glass-panel border-white/10">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`h-12 w-12 rounded-full bg-white/10 flex items-center justify-center ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs text-white/60 uppercase tracking-wide">{label}</p>
            <strong className="text-2xl font-semibold text-white">{value || 0}</strong>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="page-stack" data-testid="compliance-center">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9E — Compliance & Safety</p>
          <h2>Compliance Center</h2>
          <div className="hero-stats">
            <span>{dashboardMetrics?.total_suppressed || 0} Suppressed</span>
            <span>{dashboardMetrics?.total_consent_records || 0} Consent Records</span>
            <span>{dashboardMetrics?.today_unsubscribes || 0} Today's Unsubscribes</span>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="glass-panel flex-wrap">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="suppression">Suppression List</TabsTrigger>
          <TabsTrigger value="consent">Consent Records</TabsTrigger>
          <TabsTrigger value="logs">Compliance Logs</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard icon={ShieldAlert} label="Total Suppressed" value={dashboardMetrics?.total_suppressed} color="text-red-400" />
            <MetricCard icon={ShieldCheck} label="Consent Records" value={dashboardMetrics?.total_consent_records} color="text-green-400" />
            <MetricCard icon={Mail} label="Total Unsubscribes" value={dashboardMetrics?.total_unsubscribes} color="text-orange-400" />
          </section>

          <Card className="glass-panel border-white/10 mt-4">
            <CardHeader>
              <CardTitle className="text-white/90 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-[var(--gold)]" />
                Compliance Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div>
                    <h4 className="text-sm font-semibold text-green-300">All Systems Compliant</h4>
                    <p className="text-xs text-green-400/80 mt-1">Suppression and consent tracking active</p>
                  </div>
                  <ShieldCheck className="h-8 w-8 text-green-400" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-white/60 mb-1">Today's Suppressions Added</p>
                    <p className="text-xl font-semibold text-white/90">{dashboardMetrics?.today_suppressions || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Today's Unsubscribes</p>
                    <p className="text-xl font-semibold text-white/90">{dashboardMetrics?.today_unsubscribes || 0}</p>
                  </div>
                </div>

                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid="outreach-approval-banner">
                  <h4 className="text-sm font-semibold text-blue-300 mb-2">Manual Approval Required</h4>
                  <p className="text-xs text-blue-400/80">
                    No outreach is sent automatically. Every email/SMS draft must be approved by a Sales Manager or the Master Administrator in the Outreach Approval queue, and opted-out contacts are blocked at approval and again at send.
                  </p>
                  {outreachCounts && (
                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-white/60 mb-1">Awaiting approval</p>
                        <p className="text-xl font-semibold text-white/90">{outreachCounts.pending_approval || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 mb-1">Approved, not sent</p>
                        <p className="text-xl font-semibold text-white/90">{outreachCounts.approved || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 mb-1">Failed sends</p>
                        <p className="text-xl font-semibold text-white/90">{outreachCounts.failed || 0}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suppression List Tab */}
        <TabsContent value="suppression">
          <Card className="glass-panel border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white/90">Suppression List</CardTitle>
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="primary-btn" data-testid="add-suppression-btn">
                      <Plus size={16} />
                      Add Suppression
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0a0a0a] border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-white">Add to Suppression List</DialogTitle>
                      <DialogDescription className="text-white/60">
                        Add email or phone to prevent outreach
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-white/70">Email</Label>
                        <Input
                          value={newSuppression.email}
                          onChange={(e) => setNewSuppression({ ...newSuppression, email: e.target.value })}
                          placeholder="email@example.com"
                          className="bg-white/5 border-white/10 text-white mt-2"
                        />
                      </div>
                      <div>
                        <Label className="text-white/70">Phone</Label>
                        <Input
                          value={newSuppression.phone}
                          onChange={(e) => setNewSuppression({ ...newSuppression, phone: e.target.value })}
                          placeholder="+1 555-0100"
                          className="bg-white/5 border-white/10 text-white mt-2"
                        />
                      </div>
                      <div>
                        <Label className="text-white/70">Reason</Label>
                        <Textarea
                          value={newSuppression.reason}
                          onChange={(e) => setNewSuppression({ ...newSuppression, reason: e.target.value })}
                          placeholder="Reason for suppression..."
                          className="bg-white/5 border-white/10 text-white mt-2"
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button className="ghost-btn" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                      <Button className="primary-btn" onClick={handleAddSuppression} data-testid="confirm-add-suppression">Add</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Search by email or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>

              {loading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full bg-white/10" />)}
                </div>
              ) : suppressionList.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="h-12 w-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/60">No suppressed contacts</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {suppressionList.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 border border-white/10 rounded-lg">
                      <div>
                        {item.email && (
                          <div className="flex items-center gap-2 mb-1">
                            <Mail className="h-4 w-4 text-white/60" />
                            <span className="text-white/90">{item.email}</span>
                          </div>
                        )}
                        {item.phone && (
                          <div className="flex items-center gap-2 mb-1">
                            <Phone className="h-4 w-4 text-white/60" />
                            <span className="text-white/90">{item.phone}</span>
                          </div>
                        )}
                        <p className="text-xs text-white/60 mt-2">Reason: {item.reason}</p>
                        <p className="text-xs text-white/40">Added by {item.added_by} on {new Date(item.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button
                        className="ghost-btn"
                        onClick={() => handleRemoveSuppression(item.id)}
                        data-testid={`remove-suppression-${item.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consent Records Tab */}
        <TabsContent value="consent">
          <Card className="glass-panel border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white/90">Consent Records</CardTitle>
                <Dialog open={isAddConsentDialogOpen} onOpenChange={setIsAddConsentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="primary-btn" data-testid="log-consent-btn">
                      <Plus size={16} />
                      Log Consent
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0a0a0a] border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-white">Log Consent Record</DialogTitle>
                      <DialogDescription className="text-white/60">
                        Record consent obtained outside an automated flow (verbal, phone, paper form)
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-white/70">Email</Label>
                        <Input
                          value={newConsent.email}
                          onChange={(e) => setNewConsent({ ...newConsent, email: e.target.value })}
                          placeholder="email@example.com"
                          className="bg-white/5 border-white/10 text-white mt-2"
                        />
                      </div>
                      <div>
                        <Label className="text-white/70">Phone</Label>
                        <Input
                          value={newConsent.phone}
                          onChange={(e) => setNewConsent({ ...newConsent, phone: e.target.value })}
                          placeholder="+1 555-0100"
                          className="bg-white/5 border-white/10 text-white mt-2"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white/70">Consent Type</Label>
                          <select
                            value={newConsent.consent_type}
                            onChange={(e) => setNewConsent({ ...newConsent, consent_type: e.target.value })}
                            className="w-full mt-2 bg-white/5 border border-white/10 text-white rounded-md px-3 py-2"
                            data-testid="consent-type-select"
                          >
                            <option value="opt-in">Opt-in</option>
                            <option value="opt-out">Opt-out</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-white/70">Method</Label>
                          <select
                            value={newConsent.consent_method}
                            onChange={(e) => setNewConsent({ ...newConsent, consent_method: e.target.value })}
                            className="w-full mt-2 bg-white/5 border border-white/10 text-white rounded-md px-3 py-2"
                            data-testid="consent-method-select"
                          >
                            <option value="manual">Manual entry</option>
                            <option value="verbal">Verbal (in person / phone)</option>
                            <option value="paper_form">Paper form</option>
                            <option value="email">Email reply</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/70">Notes</Label>
                        <Textarea
                          value={newConsent.consent_notes}
                          onChange={(e) => setNewConsent({ ...newConsent, consent_notes: e.target.value })}
                          placeholder="Context for how this consent was obtained..."
                          className="bg-white/5 border-white/10 text-white mt-2"
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button className="ghost-btn" onClick={() => setIsAddConsentDialogOpen(false)}>Cancel</Button>
                      <Button className="primary-btn" onClick={handleAddConsentRecord} data-testid="confirm-log-consent">Log Consent</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>

              {loading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full bg-white/10" />)}
                </div>
              ) : consentRecords.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/60">No consent records found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {consentRecords.map((record) => (
                    <div key={record.id} className="p-4 border border-white/10 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-white/90 font-medium">{record.email || record.phone}</span>
                          <Badge className={record.consent_type === 'opt-in' ? 'ml-2 bg-green-500/20 text-green-300' : 'ml-2 bg-red-500/20 text-red-300'}>
                            {record.consent_type}
                          </Badge>
                        </div>
                        <span className="text-xs text-white/60">{record.consent_method}</span>
                      </div>
                      <div className="text-xs text-white/60">
                        <p>Source: {record.source}</p>
                        <p>Date: {new Date(record.opt_in_timestamp).toLocaleString()}</p>
                        {record.consent_notes && <p>Notes: {record.consent_notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Logs Tab */}
        <TabsContent value="logs">
          <Card className="glass-panel border-white/10">
            <CardHeader>
              <CardTitle className="text-white/90">Compliance Audit Logs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>

              {loading ? (
                <div className="space-y-2">
                  {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-12 w-full bg-white/10" />)}
                </div>
              ) : complianceLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/60">No compliance logs found</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {complianceLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 border-b border-white/5 hover:bg-white/[0.01] text-xs">
                      <div className="flex-1">
                        <Badge variant="outline" className="text-[10px] border-white/20 text-white/70 mb-1">
                          {log.action_type}
                        </Badge>
                        <p className="text-white/80">{log.email || log.phone || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white/60">{log.performed_by}</p>
                        <p className="text-white/40">{new Date(log.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComplianceCenter;
